import axios from 'axios'

export interface Badge {
  id: string
  name: string
  description: string
  icon_url: string
  criteria_type: string
  criteria_value: number
  tier: 'bronze' | 'silver' | 'gold' | 'platinum'
  created_at?: string
  updated_at?: string
}

export interface Achievement {
  id: string
  name: string
  description: string
  icon_url: string
  category: string
  points: number
  created_at?: string
  updated_at?: string
}

export interface ImpactStats {
  co2_saved: number
  waste_saved: number
  meals_rescued: number
  total_orders: number
  total_points: number
  total_users: number
}

export interface LeaderboardEntry {
  user_id: string
  user_name: string
  avatar_url: string
  points: number
  badges_count: number
  rank: number
}

export interface UserAchievements {
  user_id: string
  user_name: string
  user_email: string
  badges: (Badge & { earned: boolean; earned_at?: string })[]
  achievements: (Achievement & { earned: boolean; earned_at?: string })[]
  total_points: number
}

const API_BASE_URL = '/api'

const gamificationApi = axios.create({
  baseURL: `${API_BASE_URL}/gamification`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
gamificationApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Add response interceptor for error handling
gamificationApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const gamificationService = {
  // === Badges ===
  async getBadges(): Promise<{ success: boolean; data: Badge[] }> {
    const response = await gamificationApi.get('/badges')
    return response.data
  },

  async getBadgeById(id: string): Promise<{ success: boolean; data: Badge }> {
    const response = await gamificationApi.get(`/badges/${id}`)
    return response.data
  },

  async createBadge(data: FormData | Partial<Badge>): Promise<{ success: boolean; data: Badge; message: string }> {
    const isFormData = data instanceof FormData
    const response = await gamificationApi.post('/badges', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    })
    return response.data
  },

  async updateBadge(id: string, data: FormData | Partial<Badge>): Promise<{ success: boolean; data: Badge; message: string }> {
    const isFormData = data instanceof FormData
    const response = await gamificationApi.put(`/badges/${id}`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    })
    return response.data
  },

  async deleteBadge(id: string): Promise<{ success: boolean; message: string }> {
    const response = await gamificationApi.delete(`/badges/${id}`)
    return response.data
  },

  // === Achievements ===
  async getAchievements(): Promise<{ success: boolean; data: Achievement[] }> {
    const response = await gamificationApi.get('/achievements')
    return response.data
  },

  async getAchievementById(id: string): Promise<{ success: boolean; data: Achievement }> {
    const response = await gamificationApi.get(`/achievements/${id}`)
    return response.data
  },

  async createAchievement(data: FormData | Partial<Achievement>): Promise<{ success: boolean; data: Achievement; message: string }> {
    const isFormData = data instanceof FormData
    const response = await gamificationApi.post('/achievements', data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    })
    return response.data
  },

  async updateAchievement(id: string, data: FormData | Partial<Achievement>): Promise<{ success: boolean; data: Achievement; message: string }> {
    const isFormData = data instanceof FormData
    const response = await gamificationApi.put(`/achievements/${id}`, data, {
      headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : undefined,
    })
    return response.data
  },

  async deleteAchievement(id: string): Promise<{ success: boolean; message: string }> {
    const response = await gamificationApi.delete(`/achievements/${id}`)
    return response.data
  },

  // === Impact & Leaderboard ===
  async getImpactStats(): Promise<{ success: boolean; data: ImpactStats }> {
    const response = await gamificationApi.get('/impact/stats')
    return response.data
  },

  async getLeaderboard(limit: number = 50, offset: number = 0): Promise<{ success: boolean; data: LeaderboardEntry[] }> {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())
    params.append('offset', offset.toString())
    const response = await gamificationApi.get('/leaderboard', { params })
    return response.data
  },

  // === User Achievements ===
  async getUserAchievements(userId: string): Promise<{ success: boolean; data: UserAchievements }> {
    const response = await gamificationApi.get(`/users/${userId}/achievements`)
    return response.data
  },

  async searchUsers(query: string): Promise<{ success: boolean; data: { id: string; name: string; email: string }[] }> {
    const response = await gamificationApi.get('/users/search', { params: { q: query } })
    return response.data
  },
}

export default gamificationService
